import Axios, { AxiosError } from 'axios';
import React, {
  FC,
  SyntheticEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Form, Button } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import validate from 'validate.js';
import Alert, { AlertType } from '../components/Alert';
import AppContext from '../../contexts/AppContext';
import AuthLogo from '../components/AuthLogo';
import { IUser } from '../../types/user';
import { Counter } from '../components/Counter';
import { useRecaptcha } from '../../hooks/useRecaptcha';
import { useMergedState } from '../../hooks/useMergedState';
import { IBaseForm, IBaseState } from '../../types/types';
import { UseTranslate } from '../../hooks/Translate';
import {
  isOtpLimitError,
  otpCheckText,
  otpLimitText,
  OTP_RESEND_INTERVAL,
  RECOVERY_BLOCKED,
} from '../../utils/otpLimit';

type Params = {
  userId: string;
};

type PhoneVerificationState = Partial<IBaseState & IBaseForm>;

const PhoneVerificationPage: FC = () => {
  const { returnUrl } = useContext(AppContext);
  const params = useParams<Params>();
  const navigate = useNavigate();
  const [retryRequest, setRetryRequest] = useState(false);
  const [state, setState] = useMergedState<PhoneVerificationState>({
    errors: {},
    validated: false,
    alertProps: {
      type: AlertType.NONE,
    },
  });
  const [user, setUser] = useState<IUser>();
  const processing = useRef(false);
  const [translate] = UseTranslate();

  const [getRecaptchaToken, recaptchaLoaded] = useRecaptcha(
    'userPhoneVerification'
  );

  useEffect(() => {
    if (recaptchaLoaded) {
      getDetails(params.userId!);
    }
  }, [params.userId, recaptchaLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const getDetails = async (userId: string) => {
    try {
      const url = `${process.env.REACT_APP_CAS_API}/otp/${userId}`;
      const token = await getRecaptchaToken();
      const res = await Axios.get(url, {
        headers: {
          token: token ? token.toString() : null
        },
      });

      const userData = res.data.data;
      setUser(userData);
    } catch (e) {
      let message;
      if (e instanceof AxiosError && e.response) {
        message = e?.response?.data?.message;
      }
      setState({
        alertProps: {
          type: AlertType.ERROR,
          title: translate('cas.registration.registerError'),
          message
        },
      });
    }
  };

  const constraints = {
    token: {
      presence: {
        allowEmpty: false,
        message: translate('cas.profile.value_required'),
      },
      length: {
        minimum: 4,
        maximum: 4,
        message: translate('cas.registration.codeLength')
      },
    },
  };

  const onFormSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const values = Object.fromEntries<any>(fd.entries());

    const errors = validate(values, constraints, { fullMessages: false });
    if (errors) {
      for (const key in errors) {
        errors[key] = errors[key][0];
      }

      setState({ errors });
    } else {
      setState({ errors: {} });
      user?.action === 'password-recovery'
        ? requestPasswordRecovery(values)
        : validateUser(values);
    }
    setState({ validated: true });
  };

  const requestPasswordRecovery = async (data: any) => {
    try {
      const url = `${process.env.REACT_APP_CAS_API}/users/requestPasswordRecovery/${params.userId}`;
      const token = await getRecaptchaToken();
      const res = await Axios.post(
        url,
        {
          otp: data.token,
        },
        {
          headers: {
            token: token ? token.toString() : null
          },
        }
      );
      if (res.data.status === 'FAIL') {
        setState({
          alertProps: {
            type: AlertType.ERROR,
            title: translate('cas.registration.errorNotice'),
            message: otpCheckText(translate, res.data.data),
          },
        });
      } else {
        navigate(`/password-recovery/${params.userId}`);
      }
    } catch (e) {
      let message;
      if (e instanceof AxiosError && e.response) {
        message = e.response.data.message
      }
      setState({
        alertProps: {
          type: AlertType.ERROR,
          title: translate('cas.registration.errorNotice'),
          message,
        },
      });
    }
  };

  const validateUser = async (data: any) => {
    try {
      const url = `${process.env.REACT_APP_CAS_API}/users/validate/${params.userId}`;
      const token = await getRecaptchaToken();
      const res = await Axios.post(
        url,
        {
          otp: data.token,
        },
        {
          headers: {
            token: token ? token.toString() : ''
          },
        }
      );
      if (res.data.status === 'FAIL') {
        setState({
          alertProps: {
            type: AlertType.ERROR,
            title: translate('cas.registration.errorNotice'),
            message: otpCheckText(translate, res.data.data),
          },
        });
      } else {
        setUser(res.data.data);
        setState({
          alertProps: {
            type: AlertType.SUCCESS,
            title: translate('cas.registration.success'),
            message: res.data.data.message,
          },
        });
        returnUrl
          ? window.location.replace(returnUrl)
          : navigate(`/registerSuccess/${params.userId}`);
      }
    } catch (e) {
      let message;
      if (e instanceof AxiosError && e.response) {
        message = e.response.data.message
      }
      setState({
        alertProps: {
          type: AlertType.ERROR,
          title: translate('cas.registration.errorNotice'),
          message
        },
      });
    }
  };

  const retry = async () => {
    if (!processing.current) {
      processing.current = true;
      try {
        const url = `${process.env.REACT_APP_CAS_API}/otp/${params.userId}/refreshOTP`;
        const token = await getRecaptchaToken();
        const res = await Axios.post(
          url,
          {},
          {
            headers: {
              token: token ? token.toString() : null
            },
          }
        );
        const userData = res.data.data;
        setRetryRequest(true);
        setUser(userData);
        processing.current = false;
      } catch (e) {
        processing.current = false;
        let message: string | undefined;
        if (e instanceof AxiosError && e.response) {
          message = e.response.data.message
          if (isOtpLimitError(message)) {
            const remainingSec = e.response.data.data?.remainingSec || 0;
            // stay on the page: an already received code still works
            setUser((user) => user && {
              ...user,
              resendRemainingSec: message === OTP_RESEND_INTERVAL ? remainingSec : 0,
              blockRemainingSec: message === RECOVERY_BLOCKED ? remainingSec : 0,
            });
            message = otpLimitText(translate, message, remainingSec);
          }
        }
        setState({
          alertProps: {
            type: AlertType.ERROR,
            title: translate('cas.registration.errorNotice'),
            message
          },
        });
      }
    }
  };

  const validateOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const errors = validate({ token: event.currentTarget.value }, constraints, { fullMessages: false });
    if (errors) {
      for (const key in errors) {
        errors[key] = errors[key][0];
      }
      setState({ errors });
    } else {
      setState({ errors: {} });
    }
  };

  const validateOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['+', '-', '.', 'e'].indexOf(event.key) !== -1) {
      event.preventDefault();
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="text-center">
        <AuthLogo />
      </div>

      {user ? (
        <div className="mb-4">
          {user?.action === 'password-recovery' ? (
            <>
              <h6>{translate('cas.registration.resetPswd')}</h6>
            </>
          ) : (
            <>
              <h6>{translate('cas.registration.phoneVerification')}</h6>
              <div className="small">
                <span className="text-muted">
                  {translate('cas.registration.phoneVerifOneTimeCode')}
                </span>
                <strong className="mx-2">{user?.phoneNumber}</strong>
                <button className="link-button">
                  <Link to={`/phone/${params.userId}`}>{translate('cas.registration.change')}</Link>
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <></>
      )}

      <Alert
        type={state.alertProps!.type}
        title={state.alertProps!.title}
        message={state.alertProps!.message}
      />

      <Form validated={state.validated} onSubmit={onFormSubmit} noValidate>
        <Form.Group controlId="token" className="mb-0">
          <Form.Label className="small key-icon">
            {translate('cas.registration.inputOneTimeCode')}
          </Form.Label>
          <Form.Control
            type="number"
            placeholder={translate('cas.registration.inputCode')}
            name="token"
            isInvalid={'token' in state.errors!}
            required
            onKeyDown={validateOnKeyDown}
            onChange={validateOnChange}
          />
          <Form.Control.Feedback type="invalid">
            {state.errors?.token}
          </Form.Control.Feedback>
        </Form.Group>
        {retryRequest ? (
          <div className="text-center text-success h6">
            <svg
              width="1em"
              height="1em"
              viewBox="0 0 16 16"
              className="bi bi-check"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M10.97 4.97a.75.75 0 0 1 1.071 1.05l-3.992 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.236.236 0 0 1 .02-.022z"
              />
            </svg>
            <small>{translate('cas.registration.newCodeSuccess')}</small>
          </div>
        ) : (
          <></>
        )}

        <Counter
          seconds={60}
          onRetry={retry}
          refreshCount={user?.refreshCount}
          resendRemainingSec={user?.resendRemainingSec}
          blockRemainingSec={user?.blockRemainingSec}
        />

        <Button variant="primary" type="submit" className="w-100">
          {translate('cas.registration.confirm')}
        </Button>
      </Form>
    </div>
  );
};

export default PhoneVerificationPage;
